import React, { useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Download, ExternalLink, Filter, User } from 'lucide-react';
import { toast } from 'sonner';
import { useIsMobile } from '@/hooks/use-mobile';
import { Expenses } from '@/integrations/api';
import { ExpenseCard, statusConfig, type Expense } from './ExpenseCard';

interface EmployeeExpensesModalProps {
  open: boolean;
  onClose: () => void;
  expenses: Expense[];
  employeeName: string;
  onStatusChange: (id: string, status: Expense['status']) => void;
  onReject?: (expense: Expense) => void;
}

type SortField = 'date' | 'submittedAt';

const ALL_STATUSES = Object.keys(statusConfig) as Expense['status'][];
const DEFAULT_STATUS_FILTER: Expense['status'][] = ['pending', 'recorded'];

export function EmployeeExpensesModal({
  open,
  onClose,
  expenses,
  employeeName,
  onStatusChange,
  onReject
}: EmployeeExpensesModalProps) {
  const [statusFilter, setStatusFilter] = useState<Set<Expense['status']>>(
    new Set(DEFAULT_STATUS_FILTER)
  );
  const [sortField, setSortField] = useState<SortField>('date');
  const [bundling, setBundling] = useState(false);
  const isMobile = useIsMobile();
  const isInTeams = window.navigator.userAgent.includes('Teams');

  const toggleStatus = (status: Expense['status'], checked: boolean) => {
    setStatusFilter(prev => {
      const next = new Set(prev);
      if (checked) next.add(status); else next.delete(status);
      return next;
    });
  };

  const visibleExpenses = useMemo(() => {
    return expenses
      .filter(e => statusFilter.has(e.status))
      .slice()
      .sort((a, b) => {
        const aDate = (sortField === 'submittedAt' ? a.submittedAt : a.date) || '';
        const bDate = (sortField === 'submittedAt' ? b.submittedAt : b.date) || '';
        return bDate.localeCompare(aDate);
      });
  }, [expenses, statusFilter, sortField]);

  const receiptExpenseIds = useMemo(
    () => visibleExpenses.filter(e => e.receiptUrl).map(e => e.id),
    [visibleExpenses]
  );

  const handleDownloadAllReceipts = async () => {
    if (receiptExpenseIds.length === 0) return;
    setBundling(true);
    let blobUrl: string | null = null;
    try {
      const { url, filename } = await Expenses.downloadReceiptsBundle(receiptExpenseIds);
      blobUrl = url;

      if (isMobile || isInTeams) {
        window.open(blobUrl, '_blank', 'noopener,noreferrer');
        return;
      }

      const safeName = employeeName.replace(/[^a-zA-Z0-9-_]+/g, '_');
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename || `receipts-${safeName}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Failed to build receipt bundle:', error);
      toast.error('Failed to download receipts');
    } finally {
      if (blobUrl && !(isMobile || isInTeams)) {
        window.URL.revokeObjectURL(blobUrl);
      }
      setBundling(false);
    }
  };
  // Totals are per-currency — an employee can have expenses in DKK/EUR/USD/… and
  // summing across currencies (and labelling it "DKK") would be meaningless.
  const sumByCurrency = (list: Expense[]) =>
    list.reduce<Record<string, number>>((acc, e) => {
      const c = e.currency || 'DKK';
      acc[c] = (acc[c] || 0) + e.amount;
      return acc;
    }, {});

  const formatTotals = (totals: Record<string, number>) => {
    const entries = Object.entries(totals).filter(([, amt]) => amt > 0);
    if (entries.length === 0) return '0.00';
    return entries.map(([c, amt]) => `${amt.toFixed(2)} ${c}`).join(' • ');
  };

  const totalByCurrency = sumByCurrency(expenses);
  const unpaidExpenses = expenses.filter(e => e.status !== 'paid' && e.status !== 'rejected');
  const unpaidByCurrency = sumByCurrency(unpaidExpenses);
  const totalUnpaidLabel = formatTotals(unpaidByCurrency);
  const totalAmountLabel = formatTotals(totalByCurrency);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader className="pr-14">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-primary rounded-full flex items-center justify-center">
              <User className="h-5 w-5 text-primary-foreground" />
            </div>
            <div>
              <DialogTitle className="text-xl">{employeeName}'s Expenses</DialogTitle>
              <p className="text-sm text-muted-foreground">
                {expenses.length} total expenses • {totalUnpaidLabel} unpaid
              </p>
            </div>
          </div>
        </DialogHeader>
        
        <div className="flex-1 overflow-auto space-y-4">
          {/* Summary Card */}
          <Card>
            <CardContent className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-foreground">{expenses.length}</div>
                  <p className="text-sm text-muted-foreground">Total Expenses</p>
                </div>
                <div>
                  <div className="text-2xl font-bold text-accent">{totalUnpaidLabel}</div>
                  <p className="text-sm text-muted-foreground">Amount Unpaid</p>
                </div>
                <div>
                  <div className="text-2xl font-bold text-success">{totalAmountLabel}</div>
                  <p className="text-sm text-muted-foreground">Total Amount</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Filter & Sort Controls */}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Filter className="mr-2 h-4 w-4" />
                  Status ({statusFilter.size})
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuLabel>Filter by status</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {ALL_STATUSES.map(status => (
                  <DropdownMenuCheckboxItem
                    key={status}
                    checked={statusFilter.has(status)}
                    onCheckedChange={(checked) => toggleStatus(status, checked)}
                    onSelect={(e) => e.preventDefault()}
                  >
                    {statusConfig[status].label}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <div className="flex items-center gap-2">
              {receiptExpenseIds.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={bundling}
                  onClick={handleDownloadAllReceipts}
                >
                  {isMobile || isInTeams ? (
                    <ExternalLink className="mr-2 h-4 w-4" />
                  ) : (
                    <Download className="mr-2 h-4 w-4" />
                  )}
                  {bundling ? 'Preparing…' : `Download all receipts (${receiptExpenseIds.length})`}
                </Button>
              )}

              <Select value={sortField} onValueChange={(value) => setSortField(value as SortField)}>
                <SelectTrigger className="w-auto h-9 min-w-[10rem]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="date">Sort by payment date</SelectItem>
                  <SelectItem value="submittedAt">Sort by upload date</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Expenses List */}
          <div className="space-y-3">
            {expenses.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p>No expenses found for this employee</p>
              </div>
            ) : visibleExpenses.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p>No expenses match the selected status filter</p>
              </div>
            ) : (
              visibleExpenses.map((expense) => (
                <ExpenseCard
                  key={expense.id}
                  expense={expense}
                  onStatusChange={onStatusChange}
                  onReject={onReject}
                  showActions={true}
                  isAdminView={true}
                />
              ))
            )}
          </div>
        </div>

        {/* Close Button */}
        <div className="flex justify-end pt-4 border-t">
          <Button onClick={onClose} variant="outline">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Also export as default for better compatibility
export default EmployeeExpensesModal;